<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import StandardInput from '@/components/StandardInput.vue';
import { Constants } from '@/constants';

// Asks a superadmin for the reference of the customer's request before they
// enter its community (COCO-3425). Resolves with the reference; the API checks
// it and records it.
const props = defineProps<{ communityName: string }>();
defineEmits([...useDialogPluginComponent.emits]);

const { t } = useI18n();
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { handleSubmit, meta } = useForm({
  initialValues: { reference: '' },
  validationSchema: yup.object({
    reference: yup
      .string()
      .trim()
      .max(Constants.CustomerAccess.ReferenceMaxLength)
      .required(t('customerAccess.dialog.referenceRequired')),
  }),
});

const onSubmit = handleSubmit((values) => onDialogOK(values.reference.trim()));
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('customerAccess.dialog.title', { community: props.communityName }) }}</div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <p>
          {{ t('customerAccess.dialog.rule') }}
          <a :href="Constants.CustomerAccess.RuleUrl" target="_blank" rel="noopener">
            {{ t('customerAccess.dialog.ruleLink') }}
          </a>
        </p>
        <standard-input
          name="reference"
          :label="t('customerAccess.dialog.referenceLabel')"
          :hint="t('customerAccess.dialog.referenceHint')"
          autofocus
          @keyup.enter="onSubmit"
        />
        <p class="text-caption text-grey-8 q-mt-md">{{ t('customerAccess.dialog.duration') }}</p>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('customerAccess.dialog.enter')" :disabled="!meta.valid" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
